import { FormControl, FormLabel, Stack, Switch, Text, useDisclosure } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import {
  IdentityType,
  useCreateCompanyRepresentativeMutation,
  useCreateIndividualRepresentativeMutation,
  useDeleteRepresentativeMutation,
} from '@blockpulse3/graphql/hooks';
import {
  SwitchIdentityType,
  SwitchIdentityTypeChange,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { representativeFormDefaults } from '../schema';
import { IRepresentativeForm } from '../types';
import { getUserRepresentative } from '../utils';
import { LegalRepresentativeInformationsForm } from './LegalRepresentativeInformationsForm';
import { NaturalRepresentativeInformationsForm } from './NaturalRepresentativeInformationsForm';

type Props = {
  /* ** Id of the representative ** */
  id: string | null;
  /* ** IdentityType of the form ** */
  type?: IdentityType;
  /* ** Default form values for the Legal and Natural ** */
  defaultValues?: IRepresentativeForm;
  /* ** Is the Cancel button disabled ** */
  shouldCancel?: boolean;
  /* ** Callback of the Cancel button ** */
  onCancel?: () => void;
  /* ** Callback on submit complete ** */
  onSubmitComplete?: () => void;
  /* ** Callback on submit error ** */
  onSubmitError?: () => void;
};

export const RepresentantInformationsForm = React.forwardRef<HTMLFormElement, Props>(
  (props, ref) => {
    const {
      id = null,
      defaultValues = representativeFormDefaults,
      shouldCancel = false,
      onCancel = noop,
      onSubmitComplete = noop,
      onSubmitError = noop,
    } = props;

    const i18nGeneral = useTranslations('General');
    const i18nRepresentantInformationsStep = useTranslations('SPVRepresentantInformationsStep');

    const errorToast = useErrorToast();
    const successToast = useSuccessToast();

    const { companyId = '' } = useParams();
    const { user } = useUser();

    const [type, setType] = useState<IdentityType>(defaultValues.type);

    const [formValues, setFormValues] = useState<IRepresentativeForm>(defaultValues);

    const [createCompanyRepresentative] = useCreateCompanyRepresentativeMutation();
    const [createIndividualRepresentative] = useCreateIndividualRepresentativeMutation();
    const [deleteRepresentative] = useDeleteRepresentativeMutation();

    /* ** Disclosure to control the natural switch ** */
    const { isOpen, onOpen, onClose, onToggle } = useDisclosure({
      defaultIsOpen: false,
    });

    const handleUserIdentityChange = (): void => {
      if (type === IdentityType.COMPANY) {
        setType(IdentityType.INDIVIDUAL);
        onOpen();
      } else {
        onToggle();
      }
      if (!isOpen) {
        /* ** Fill with user values ** */
        const userValues = getUserRepresentative(user);
        setFormValues(userValues);
      }
    };

    const handleIdentityTypeChange = ({ next }: SwitchIdentityTypeChange): void => {
      if (next === IdentityType.COMPANY) {
        onClose();
      }
      setType(next);
      /* ** Reset form on type change, forcing update ** */
      setFormValues(representativeFormDefaults);
    };

    const deletePreviousRepresentative = async (id: string | null): Promise<void> => {
      if (id) {
        await deleteRepresentative({ variables: { representativeId: id } });
      }
    };

    const handleLegalFormSubmit = async (data: IRepresentativeForm): Promise<void> => {
      /* ** Delete previous submitted representative with its id ** */
      await deletePreviousRepresentative(id);
      /* ** Create new representative ** */
      createCompanyRepresentative({
        variables: {
          createCompanyRepresentativeInput: {
            role: data.role,
            companyId,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            position: data.position,
            address: {
              line: data.address.line,
              city: data.address.city,
              postalCode: data.address.postalCode,
              country: data.address.country.value,
            },
            name: data.name,
            corporateForm: data.corporateForm,
            shareCapital: data.shareCapital,
            registrationEntity: data.registrationEntity,
            registrationNumber: data.registrationNumber,
          },
        },
        onCompleted: () => {
          successToast({ title: i18nRepresentantInformationsStep('Tooltip.Success') });
          onSubmitComplete();
        },
        onError: () => {
          errorToast({ title: i18nRepresentantInformationsStep('Tooltip.Error') });
          onSubmitError();
        },
      });
    };

    const handleNaturalFormSubmit = async (data: IRepresentativeForm): Promise<void> => {
      /* ** Delete previous submitted representative with its id ** */
      await deletePreviousRepresentative(id);
      /* ** Create new representative ** */
      createIndividualRepresentative({
        variables: {
          createIndividualRepresentativeInput: {
            role: data.role,
            companyId,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            nationality: data.nationality.value,
            birthdate: data.birthdate,
            birthplace: data.birthplace,
            address: {
              line: data.address.line,
              city: data.address.city,
              postalCode: data.address.postalCode,
              country: data.address.country.value,
            },
          },
        },
        onCompleted: async () => {
          successToast({ title: i18nRepresentantInformationsStep('Tooltip.Success') });
          onSubmitComplete();
        },
        onError: () => {
          errorToast({ title: i18nRepresentantInformationsStep('Tooltip.Error') });
          onSubmitError();
        },
      });
    };

    /* ** Cancel whatever representant form is being rendered ** */
    const handleFormCancel = (): void => {
      onCancel();
    };

    return (
      <Stack spacing="4">
        <SwitchIdentityType
          direction={{ base: 'column', md: 'row' }}
          value={type}
          onChange={handleIdentityTypeChange}
        />
        <Stack direction={{ base: 'column', md: 'row' }}>
          <FormControl alignItems="center" display="flex">
            <Switch id="isMe" isChecked={isOpen} onChange={handleUserIdentityChange} />
            <FormLabel htmlFor="isMe" mb="0" ml="2">
              <Text>
                {i18nGeneral('ItsMe')},
                <Text as="span" fontWeight="bold">
                  {` ${user?.firstName} ${user?.lastName}`}
                </Text>
              </Text>
            </FormLabel>
          </FormControl>
        </Stack>
        {type === IdentityType.COMPANY ? (
          <LegalRepresentativeInformationsForm
            defaultValues={formValues}
            ref={ref}
            shouldCancel={shouldCancel}
            onCancel={handleFormCancel}
            onSubmit={handleLegalFormSubmit}
          />
        ) : (
          <NaturalRepresentativeInformationsForm
            defaultValues={formValues}
            ref={ref}
            shouldCancel={shouldCancel}
            onCancel={handleFormCancel}
            onSubmit={handleNaturalFormSubmit}
          />
        )}
      </Stack>
    );
  },
);

RepresentantInformationsForm.displayName = 'RepresentantInformationsForm';

export type RepresentantInformationsFormProps = Props;
